import logger from '../utils/logger';
import React, { createContext, useContext, useState, useEffect } from 'react';

interface CookieConsent {
  analytics: boolean;
  advertising: boolean;
  preferences: boolean;
}

interface CookieContextType {
  consent: CookieConsent | null;
  setConsent: (consent: CookieConsent) => void;
  hasConsented: boolean;
  loadScripts: () => void;
}

const CookieContext = createContext<CookieContextType | undefined>(undefined);

export const useCookieConsent = () => {
  const context = useContext(CookieContext);
  if (context === undefined) {
    throw new Error('useCookieConsent must be used within a CookieProvider');
  }
  return context;
};

interface CookieProviderProps {
  children: React.ReactNode;
}

export const CookieProvider: React.FC<CookieProviderProps> = ({ children }) => {
  const [consent, setConsentState] = useState<CookieConsent | null>(null);
  const [hasConsented, setHasConsented] = useState(false);

  useEffect(() => {
    // Check for existing consent
    const savedConsent = localStorage.getItem('bytescan-cookie-consent');
    if (savedConsent) {
      try {
        const parsed = JSON.parse(savedConsent);
        setConsentState(parsed);
        setHasConsented(true);
      } catch (error) {
        logger.error('Failed to parse saved consent:', error);
      }
    }
  }, []);

  const setConsent = (newConsent: CookieConsent) => {
    setConsentState(newConsent);
    setHasConsented(true);
    localStorage.setItem('bytescan-cookie-consent', JSON.stringify(newConsent));
    
    // Load scripts based on consent
    loadScripts();
  };

  const loadScripts = () => {
    if (!consent) return;

    // Load Google Analytics if analytics consent given
    if (consent.analytics && !window.gtag) {
      const script = document.createElement('script');
      script.async = true;
      script.src = 'https://www.googletagmanager.com/gtag/js?id=AW-17317045804';
      document.head.appendChild(script);

      script.onload = () => {
        if (!window.dataLayer) {
          window.dataLayer = [];
        }
        function gtag(...args: any[]) {
          window.dataLayer!.push(args);
        }
        gtag('js', new Date());
        gtag('config', 'AW-17317045804');
        window.gtag = gtag;
      };
    }

    // Load Google AdSense if advertising consent given
    if (consent.advertising && !window.adsbygoogle) {
      const script = document.createElement('script');
      script.async = true;
      script.src = 'https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=ca-pub-5458499378481903';
      script.crossOrigin = 'anonymous';
      document.head.appendChild(script);
    }
  };

  // Load scripts when consent changes
  useEffect(() => {
    if (hasConsented) {
      loadScripts();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [consent, hasConsented]);

  const value: CookieContextType = {
    consent: consent,
    setConsent,
    hasConsented,
    loadScripts,
  };

  return (
    <CookieContext.Provider value={value}>
      {children}
    </CookieContext.Provider>
  );
};

// Extend Window interface for TypeScript
declare global {
  interface Window {
    gtag?: (...args: any[]) => void;
    dataLayer?: any[];
    adsbygoogle: any[];
  }
} 